import { useCallback, useEffect, useRef, useState } from "react";
import type { UserProfile } from "@chuzi/shared/types";
import { CreditBadge } from "@chuzi/shared/ui";
import { DirectorAvatar } from "./director-avatar";
import { api } from "../lib/api";

export type DirectorMenuAction = "profile" | "logout";

interface CreditBalance {
  watch: number;
  create: number;
}

const COSMOS_BG = "rgba(10,16,32,0.92)";
const COSMOS_BORDER = "rgba(126,184,255,0.35)";
const COSMOS_TEXT = "#e8f0ff";
const COSMOS_HOVER = "rgba(126,184,255,0.12)";

/**
 * Top-left director badge: avatar circle that opens a cosmos-styled
 * dropdown with "Director profile", "Create", and "Logout" actions.
 * Displays viewer & creator credit badges alongside the avatar.
 */
export function DirectorMenu({
  user,
  onAction,
}: {
  user: UserProfile | null;
  onAction: (action: DirectorMenuAction) => void;
}) {
  const [open, setOpen] = useState(false);
  const [credits, setCredits] = useState<CreditBalance | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const refreshCredits = useCallback(() => {
    if (!user) return;
    api.credits
      .balance()
      .then((res) => setCredits(res.balance))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    refreshCredits();
  }, [refreshCredits]);

  useEffect(() => {
    function onCreditsUpdated(e: Event) {
      const detail = (e as CustomEvent<CreditBalance>).detail;
      if (detail) setCredits(detail);
      else refreshCredits();
    }
    window.addEventListener("chuzi:credits-updated", onCreditsUpdated);
    return () => window.removeEventListener("chuzi:credits-updated", onCreditsUpdated);
  }, [refreshCredits]);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function pick(action: DirectorMenuAction) {
    setOpen(false);
    onAction(action);
  }

  return (
    <div
      ref={rootRef}
      style={{
        position: "absolute",
        top: 16,
        left: 16,
        zIndex: 110,
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
      }}
    >
      <button
        type="button"
        aria-label="Open director menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: 96,
          height: 96,
          borderRadius: "50%",
          border: `1px solid ${COSMOS_BORDER}`,
          background: COSMOS_BG,
          padding: 0,
          cursor: "pointer",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: COSMOS_TEXT,
          flexShrink: 0,
          backdropFilter: "blur(8px)",
          boxShadow: open
            ? "0 0 0 2px rgba(126,184,255,0.45)"
            : "0 0 0 1px rgba(126,184,255,0.18)",
          transition: "box-shadow 120ms ease",
        }}
      >
        {user?.avatar_url ? (
          <img
            src={user.avatar_url}
            alt={user.name || "Director"}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              padding: 6,
              boxSizing: "border-box",
            }}
          >
            <DirectorAvatar
              seed={user?.id ?? "anonymous"}
              realm={user?.realm ?? null}
            />
          </div>
        )}
      </button>

      {credits ? (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 8,
            paddingTop: 6,
          }}
        >
          <CreditBadge
            role="viewer"
            value={credits.watch}
            style={{ paddingHorizontal: 18, paddingVertical: 12 }}
          />
          <CreditBadge
            role="creator"
            value={credits.create}
            style={{ paddingHorizontal: 18, paddingVertical: 12 }}
          />
        </div>
      ) : null}

      {open ? (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: 104,
            left: 0,
            minWidth: 220,
            background: COSMOS_BG,
            border: `1px solid ${COSMOS_BORDER}`,
            borderRadius: 12,
            backdropFilter: "blur(8px)",
            padding: 8,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            boxShadow: "0 12px 28px rgba(0,0,0,0.45)",
          }}
        >
          {user ? (
            <div
              style={{
                padding: "8px 10px 6px",
                borderBottom: `1px solid ${COSMOS_BORDER}`,
                marginBottom: 4,
                fontSize: 13,
                fontWeight: 700,
                color: COSMOS_TEXT,
              }}
            >
              {user.name || "Director"}
            </div>
          ) : null}
          <MenuItem onClick={() => pick("profile")}>Director profile</MenuItem>
          <MenuItem onClick={() => pick("logout")} tone="danger">
            Logout
          </MenuItem>
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({
  children,
  onClick,
  tone = "default",
}: {
  children: React.ReactNode;
  onClick: () => void;
  tone?: "default" | "danger";
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      role="menuitem"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      style={{
        textAlign: "left",
        background: hover ? COSMOS_HOVER : "transparent",
        color: tone === "danger" ? "#ff9aa2" : COSMOS_TEXT,
        border: "none",
        borderRadius: 8,
        padding: "8px 10px",
        fontSize: 13,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}
